import { type AxiosResponse } from 'axios';

import { type Country } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

import { BetslipType } from '~/components/betslip/betslip';
import store from '~/components/betslip/betslip.store';
import ODDS_FORMAT from '~/components/betslip/enums/odds_format';
import type IBet from '~/components/betslip/interfaces/IBet';
import type IOdds from '~/components/betslip/interfaces/IOdds';
import ConfigService, { type IConfig } from '~/stores/configuration';
import { BET_ADDED, BETSLIP_OPEN, BETSLIPCOOKIE } from '~/stores/constants';
import type { Repub } from '~/types';
import { getRepubFromURL } from '~/utils/getRepubFromURL';

import { $http } from '../util/$http';

import { CookieService } from './cookie-actions';
import LinkService from './link-actions';
import LocalStorageService from './localstorage-actions';
import TrackingActions, {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from './tracking-actions';

interface BookieOdds {
	betIds: Array<number>;
	bookmakerCode: string;
	bookmakerType: string;
	oddsDecimal: number;
	oddsFractional: string;
	oddsUs: string;
}

class BettingActions {
	private cookieService: CookieService;
	private localstore: LocalStorageService;
	private trackingActions: TrackingActions;
	private linkService: LinkService;
	private configService: IConfig;

	private updateClient: (key: string, val: any) => void;
	private baseUrl: string;

	constructor() {
		this.cookieService = new CookieService();
		this.localstore = new LocalStorageService();
		this.trackingActions = new TrackingActions();
		this.linkService = new LinkService();
		this.configService = ConfigService.getInstance().getConfig();

		this.updateClient = store.getState().updateClient;
		this.baseUrl = store.getState().server.config.baseUrl;
	}

	public getBestOddsFromBet(odds: IOdds[]) {
		if (!odds?.length) {
			return {};
		}

		const bestOdds = odds.reduce((prev, current) =>
			prev.oddsDecimal > current.oddsDecimal ? prev : current,
		);

		return {
			bestOddsDecimal: bestOdds.oddsDecimal,
			bestOddsUs: bestOdds.oddsUs,
			bestOddsFractional: bestOdds.oddsFractional,
			bestBookie: bestOdds.bookmakerCode,
		};
	}

	public getBestOdds(bet: any): string {
		let betOdds;

		switch (this.getOddsFormat()) {
			case ODDS_FORMAT.AMERICAN:
				betOdds = bet.bestOddsUs || bet.oddsUs;
				if (betOdds > 0) {
					betOdds = `+${betOdds}`;
				}
				break;
			case ODDS_FORMAT.DECIMAL:
				betOdds = bet.bestOddsDecimal || bet.bestOpeningOddsDecimal;
				break;
			case ODDS_FORMAT.FRACTIONAL:
				betOdds = bet.bestOddsFractional || bet.oddsFractional;
				break;
		}

		return betOdds || '--';
	}

	public convertAmericanToFractional(oddsAmerican: string): string {
		const oddsDecimal = this.convertAmericanToDecimal(oddsAmerican);

		return this.convertDecimalOddsToFractional(oddsDecimal);
	}

	public convertAmericanToDecimal(oddsAmerican: string): number {
		const numericalValue = Number(oddsAmerican);
		const decimal =
			numericalValue >= 0 ? 1 + numericalValue / 100 : 1 - 100 / numericalValue;
		const fixedDecimal = ((decimal * 100) / 100).toFixed(4);

		return Number(fixedDecimal);
	}

	public convertFractionalToAmerican(oddsFractional: string): string {
		const oddsDecimal = this.convertFractionalToDecimal(oddsFractional);

		return this.convertDecimalToAmerican(oddsDecimal);
	}

	public convertFractionalToDecimal(fractionalOdds: string): number {
		const fractionParts = fractionalOdds.split('/');
		if (fractionParts.length < 2) {
			return 0;
		}
		const decimal = Number(fractionParts[0]) / Number(fractionParts[1]) + 1;
		const fixedDecimal = (Math.round(decimal * 100) / 100).toFixed(2);

		return Number(fixedDecimal);
	}

	public convertDecimalToAmerican(oddsDecimal: number): string {
		let convertedOddsToUS;
		if (oddsDecimal >= 2) {
			convertedOddsToUS = (oddsDecimal - 1) * 100;
			return `${convertedOddsToUS.toFixed(0)}`;
		}
		if (oddsDecimal < 2) {
			convertedOddsToUS = -100 / (oddsDecimal - 1);
			return convertedOddsToUS.toFixed(0);
		}

		return '';
	}

	public convertDecimalOddsToFractional(oddsDecimal: number) {
		if (oddsDecimal > 3) {
			return `${Math.floor(oddsDecimal - 1)}/1`;
		}
		const reduce = (numerator: number, denominator: number) => {
			let gcd: any = function gcd(a, b) {
				return b ? gcd(b, a % b) : a;
			};
			gcd = gcd(numerator, denominator);
			return [numerator / gcd, denominator / gcd];
		};
		const temp: any = oddsDecimal - 1;
		const number = Number(Number(temp.toFixed(2) * 100).toFixed(2));
		const simplestForm = reduce(number, 100);

		if (simplestForm.length < 2) {
			return '';
		}

		return `${simplestForm[0]}/${simplestForm[1]}`;
	}

	public getTotalOdds(odds: any, opening = false): string {
		let betOdds;
		switch (this.getOddsFormat()) {
			case ODDS_FORMAT.AMERICAN:
				betOdds = opening ? odds.openingOddsUS : odds.oddsUs;
				break;
			case ODDS_FORMAT.DECIMAL:
				betOdds = opening ? odds.openingOddsDecimal : odds.oddsDecimal;
				betOdds = betOdds ? betOdds.toFixed(2) : '--';
				break;
			case ODDS_FORMAT.FRACTIONAL:
				betOdds = opening ? odds.openingOddsFractional : odds.oddsFractional;
				break;
		}
		return betOdds || '--';
	}

	public getOdds(odds: any, opening = false): string {
		let betOdds;

		switch (this.getOddsFormat()) {
			case ODDS_FORMAT.AMERICAN:
				betOdds = opening ? odds.openingOddsUS : odds.oddsUs;
				if (betOdds > 0 && !betOdds.includes('+')) {
					betOdds = `+${betOdds}`;
				}
				break;
			case ODDS_FORMAT.DECIMAL:
				betOdds = opening ? odds.openingOddsDecimal : odds.oddsDecimal;
				betOdds = betOdds ? betOdds.toFixed(2) : '--';
				break;
			case ODDS_FORMAT.FRACTIONAL:
				betOdds = opening ? odds.openingOddsFractional : odds.oddsFractional;
				break;
		}
		return betOdds || '--';
	}

	public getOddsFormat(): ODDS_FORMAT {
		const oddsCookieValue = this.cookieService
			.select('odds_format')
			.getString();

		if (oddsCookieValue.toUpperCase() in ODDS_FORMAT) {
			return oddsCookieValue as ODDS_FORMAT;
		}

		return ODDS_FORMAT.AMERICAN;
	}

	public getBetslipCookieArray(): [string, string][] {
		const betslipCookieArray = this.cookieService
			.select('intBetslip')
			.getArray(',', (item: string) => {
				return item.split('~');
			});

		// Ensure we filter out non-tuple data
		return betslipCookieArray.filter(c => !!c?.[1]);
	}

	public getBetslipBetIdsFromCookieArray(): string[] {
		const betslipBetIdsFromCookieArray = this.getBetslipCookieArray().map(
			c => c[0],
		);

		return betslipBetIdsFromCookieArray;
	}

	public getBetCountFromBetslipCookies(betslipCookieArray: Array<any>): number {
		return betslipCookieArray.length;
	}

	public getBetSelectedBookieFromLocalStore(betId: string) {
		const localStorageBetslip =
			this.localstore.select('intBetslip').getJSON() || {};
		// @ts-ignore
		if (typeof localStorageBetslip[betId] !== 'undefined') {
			return localStorageBetslip[betId].selectedBookie;
		}

		return null;
	}

	public addOrRemoveFromBetslip(
		betArray: any[],
		matchBetIds?: any[],
		bookie?: string,
	): void {
		const cookieBetIdArray = this.getBetslipCookieArray();
		const localStorageBetslip =
			this.localstore.select('intBetslip').getJSON() || {};

		let betIndex: number;
		// let betIdToRemove: number;
		const trackingObject = {
			event: TRACKING_EVENTS.ADD_TO_BETSLIP,
			eventCategory: TRACKING_CATEGORIES.BETTING,
			eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.ADD_TO_BETSLIP],
			eventLabel: bookie,
		};

		for (let i = 0; i < betArray.length; i++) {
			// Add or remove the ocBetId arg depending on its presence in the betIdArray.
			betIndex = cookieBetIdArray
				.map(item => item[0])
				.indexOf(betArray[i].betId.toString());

			if (betIndex === -1) {
				trackingObject.eventLabel = bookie;
				// Push the bet into the array with a 1 for "selected".
				cookieBetIdArray.push([betArray[i].betId.toString(), 1]);
				// Add selectedBookie so it is retrievable via getBetSelectedBookieFromLocalStore()
				betArray[i].selectedBookie = bookie;
				localStorageBetslip[betArray[i].betId] = betArray[i];

				if (typeof matchBetIds !== 'undefined') {
					// eslint-disable-next-line array-callback-return
					matchBetIds.map(matchBet => {
						if (matchBet !== betArray[i].betId) {
							const removedBetIndex = cookieBetIdArray
								.map(item => item[0])
								.indexOf(matchBet.toString());
							if (removedBetIndex !== -1) {
								cookieBetIdArray.splice(removedBetIndex, 1);
								// Remove from localStorage
								delete localStorageBetslip[matchBet];
							}
						}
					});
				}

				if (cookieBetIdArray.length === 1) {
					this.updateClient(BETSLIP_OPEN, true);
					this.trackBetSlip(false, betArray);
				}
			} else {
				trackingObject.event = TRACKING_EVENTS.REMOVE_BET;
				trackingObject.eventAction =
					TRACKING_ACTIONS[TRACKING_EVENTS.REMOVE_BET];
				trackingObject.eventLabel = '';
				// Remove from cookie
				cookieBetIdArray.splice(betIndex, 1);

				// Remove from localStorage
				delete localStorageBetslip[betArray[i].betId];
				// betIdToRemove = i;
			}
			this.trackingActions.eventTracking(trackingObject);
		}

		if (cookieBetIdArray.length === 0) {
			this.updateClient(BETSLIP_OPEN, false);
		}

		this.updateClient(BET_ADDED, true);
		// Store mini betslip in localStorage to make odds avaialbe for estimated betslip odds.
		this.localstore.select('intBetslip').set(localStorageBetslip);

		// Update cookie and store
		const newCookieString = this.cookieService.setArrayString(cookieBetIdArray);
		this.updateClient(BETSLIPCOOKIE, newCookieString);

		// TOOD: update history
		return;

		// // UDPATE history api state
		// // Reset client & server cookie for state
		// const currentState = history.state;
		// // currentState.client[CONFIGDATA].intBetslipCookie = currentState.server[
		// //     CONFIGDATA
		// // ].intBetslipCookie = newCookieString;

		// // This is for multi betslip sharing, it won't appear on normal user journeys (see template-controller.tsx for the code relating to that)
		// // TODO - This isn't actually working so for now I am commenting out (G) - Bug raised so when it's fixed add this back in - T17499
		// // if (currentState.server.betslipData) {
		// // 	//Remove bet from betslip data as no call is made to the server when history.forward is called.
		// // 	currentState.server.betslipData.bets = currentState.server.betslipData.bets.filter((bet: ISingleBet) => bet.betId !== betArray[betIdToRemove].betId);
		// // }

		// const pathFix =
		//   currentState.client[CONFIGDATA].currentPath === "homepage"
		//     ? ""
		//     : currentState.client[CONFIGDATA].addOrRemoveFromBetslip;
		// // Place the manipulated history state back into state DataStore.
		// history.replaceState(currentState, null, pathFix);
	}

	public trackBetSlip = (override = false, betSlip: any[]) => {
		// Redeye
		let bookie = '';
		let odds = '';
		let name = '';
		let betCount = 0;

		const localStorageBetslip =
			this.localstore.select('intBetslip').getJSON() || {};
		const betslipCount = Object.keys(localStorageBetslip).length;

		if (betslipCount === 0 || override) {
			const isAcca = betSlip.length > 1;
			for (let i = 0; i < betSlip.length; i++) {
				const bet = betSlip[i];
				betCount += 1;
				const join = betCount !== 1 ? '&' : '';
				bookie += `${join}bookie${betCount}=${bet.bestOddsBookmakers}`;
				odds += `${join}bet_odds${betCount}=${bet.bestOddsDecimal}`;
				name += `${join}bet_name${betCount}=${bet.name}`;
			}

			this.trackingActions.eventTracking({
				event: TRACKING_EVENTS.BET_SLIP_VIEW,
				eventCategory: TRACKING_CATEGORIES.BETTING,
				eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_VIEW],
				eventLabel: isAcca ? 'multi_bets' : 'single_bet',
				bet: {
					bookmaker: bookie,
					odds,
					name,
				},
				accumulator: isAcca ? 'yes' : 'no',
			});
		}
	};

	public calculateOddsAndBestBookie(
		betslipData: Array<IBet>,
		betslipType: BetslipType,
	): Array<BookieOdds> {
		const accumulatedOdds: Array<BookieOdds> = [];

		let betId: number;
		let bookie: string;
		let bookmakerType: string;
		let oddsDec: number;
		let oddsFrac: string;
		let oddsUs: string;
		let bookieIndex: number;

		let i: number;
		let j: number;
		// let k: number;
		let isSelected: boolean;

		const bets = betslipData.map(bet => {
			return {
				...bet,
				...this.getBestOddsFromBet(bet.odds),
			};
		});

		// Loop through each bet and extract betId.
		for (i = 0; i < bets.length; i++) {
			if (
				betslipType === BetslipType.single ||
				(bets[i].uiOptions && betslipType === BetslipType.multiple)
			) {
				betId = bets[i].betId;

				// Loop through odds for each bet and extract bookie and bookie odds.
				for (j = 0; j < bets[i].odds.length; j++) {
					bookie = bets[i].odds[j].bookmakerCode;
					bookmakerType = bets[i].odds[j].bookmakerType;

					isSelected = bets[i].uiOptions.selected;
					// Dont include the bet in calculations if not selected.
					if (isSelected) {
						oddsDec = bets[i].odds[j].oddsDecimal;
						oddsFrac = bets[i].odds[j].oddsFractional;
						oddsUs = bets[i].odds[j].oddsUs;
					} else {
						oddsDec = 1;
						oddsFrac = '';
						oddsUs = '';
					}

					// Loop through current array of accumulated odds and see
					// if bookiemaker is already in the array with some odds.
					bookieIndex = accumulatedOdds
						.map(item =>
							typeof item.bookmakerCode !== 'undefined'
								? item.bookmakerCode
								: '',
						)
						.indexOf(bookie);

					// If the bookie was in the array increment their odds.
					if (bookieIndex > -1) {
						accumulatedOdds[bookieIndex].oddsDecimal *= oddsDec;
						accumulatedOdds[bookieIndex].oddsUs = this.convertDecimalToAmerican(
							accumulatedOdds[bookieIndex].oddsDecimal,
						);
						accumulatedOdds[bookieIndex].oddsFractional =
							this.convertDecimalOddsToFractional(
								accumulatedOdds[bookieIndex].oddsDecimal,
							);
						if (isSelected) {
							accumulatedOdds[bookieIndex].betIds.push(betId);
						}

						// If the bookie wasn't found in the array then add an entry for them.
					} else {
						const tempAccaOdds = {
							bookmakerCode: bookie,
							oddsDecimal: oddsDec,
							oddsFractional: oddsFrac,
							oddsUs,
							betIds: [],
							bookmakerType,
						};
						if (isSelected) {
							tempAccaOdds.betIds.push(betId);
						}
						accumulatedOdds.push(tempAccaOdds);
					}
				}
			}
		}

		// Sort the acca entries based on odds in descending order.
		return accumulatedOdds.sort((a, b) => b.oddsDecimal - a.oddsDecimal);
	}

	public placeBet(bookie: string, betIds: Array<number>): void {
		try {
			const repub: Repub = getRepubFromURL(new URL(this.baseUrl));
			const { CLICKOUT_URL, OC_API_KEY } = window.ENV;
			const apiKey = OC_API_KEY[repub as keyof typeof OC_API_KEY];

			const betUrl = `${CLICKOUT_URL}/splash/clickout?betIds=${betIds.join('%7C')}&bookieCode=${bookie}&apiKey=${apiKey}`;

			this.linkService.clickout(betUrl, bookie, 'bet', true);
			const betIdsCount = betIds.length;
			this.trackingActions.eventTracking({
				event: TRACKING_EVENTS.BET_PLACED,
				eventCategory: TRACKING_CATEGORIES.BETTING,
				eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_PLACED],
				eventLabel: betIdsCount === 1 ? 'Place Single Bet' : 'Place Multi Bet',
				bet: {
					stake: '',
					accumulator: betIdsCount > 1 ? 'yes' : 'no',
					onsite: 'no',
					saleConfirm: 'no',
					custref: '',
					bookie,
				},
			});
		} catch (error) {
			if (error instanceof Error) {
				console.error(error);
				throw new Error(error.message);
			} else {
				throw error;
			}
		}
	}

	public clearBetslip(localTeardownCallback: () => void): void {
		const bets = this.getBetslipCookieArray().map(
			// eslint-disable-next-line no-new-object
			bet => new Object({ betId: Number(bet[0]) }),
		);
		this.addOrRemoveFromBetslip(bets);
		localTeardownCallback();
	}

	public syncBetslips(): void {
		const cookieBetslip = this.cookieService.select('intBetslip').getString();
		const localStoreBetslip =
			this.localstore.select('intBetslip').getJSON() || {};

		const betsToDelete: Array<string> = [];

		for (const key in localStoreBetslip) {
			if (cookieBetslip.indexOf(key.toString()) === -1) {
				betsToDelete.push(key);
			}
		}

		for (let i = 0; i < betsToDelete.length; i++) {
			delete localStoreBetslip[betsToDelete[i]];
		}

		this.localstore.select('intBetslip').set(localStoreBetslip);
	}

	public getMultiBetslipAPI(
		betIds: number[],
		countryCode: Country,
		state: StateCode | '',
		completion: (success: any, res: any) => void,
	): void {
		const originUrl = window.location.origin;
		$http({
			method: 'GET',
			url: `${originUrl}/api/cx/v1/betslip?betIds=${betIds}`,
			headers: {
				'subdivision-code': state,
				'country-code': countryCode,
				repub: getRepubFromURL(new URL(this.baseUrl)),
			},
		})
			.then((res: AxiosResponse) => {
				completion({ success: true }, res.data);
			})
			.catch((e: any) => {
				console.log(e);
				completion({ success: false }, e);
			});
	}

	public getSingleBetslipAPI(
		betIds: number[],
		countryCode: Country,
		state: StateCode | '',
		completion: (success: any, res: any) => void,
	) {
		const originUrl = window.location.origin;

		$http({
			method: 'GET',
			url: `${originUrl}/api/cx/v1/betslip?betIds=${betIds}`,
			headers: {
				'subdivision-code': state,
				'country-code': countryCode,
				repub: getRepubFromURL(new URL(this.baseUrl)),
			},
		})
			.then((res: AxiosResponse) => {
				completion({ success: true }, res.data);
			})
			.catch((e: any) => {
				console.log(e);
				completion({ success: false }, e);
			});
	}

	public changeOddsFormat(newOddsFormat: string): void {
		if (!(newOddsFormat.toUpperCase() in ODDS_FORMAT)) {
			return;
		}

		const cookie = this.cookieService.select('odds_format');

		cookie.set(newOddsFormat, new Date().getTime() + 86400000);
		this.trackPageView(newOddsFormat as ODDS_FORMAT);
	}

	private trackPageView(newOddsFormat: ODDS_FORMAT): void {
		this.trackingActions.eventTracking({
			event: TRACKING_EVENTS.CHANGE_ODDS_FORMAT,
			eventCategory: TRACKING_CATEGORIES.ODDS_FORMAT,
			eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.CHANGE_ODDS_FORMAT],
			eventLabel: newOddsFormat,
		});
	}
}

export default BettingActions;
